import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  EMPTY,
  Observable,
  catchError,
  switchMap,
  takeUntil,
  throwError,
} from 'rxjs';
import { AuthApiService } from '../services/auth-api.service';
import { ClientStorageService } from '../services/client-storage.service';
import { AuthorizationData } from '../models/authorization-data.model';
import { Store } from '@ngxs/store';
import { SetIsAuthenticated } from '@app/shared/state/user-context/user-context.action';
import { ToastrService } from 'ngx-toastr';
import { hasAccessTokenExpired } from '../utils/token.util';
import { AuthContextSelectors } from '@app/shared/state/auth-context/auth-context.selectors';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  //#clientStorageService = inject(ClientStorageService);
  #authService = inject(AuthApiService);
  #store = inject(Store)
  #toastrService = inject(ToastrService)
  hasSignedout = false

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let authData: AuthorizationData = this.#store.selectSnapshot(AuthContextSelectors.authContext)
    const skipToken = request.headers.has('X-Skip-Token')

    // Check 'X-Skip-Token' header to pass request through without bearer token (for anonymous requests).
    if (skipToken) {
      const headers = request.headers.delete('X-Skip-Token');
      const modifiedRequest = request.clone({ headers });

      return next.handle(modifiedRequest);
    }

    if (!authData || !authData?.refreshToken) {
      this.#store.dispatch(new SetIsAuthenticated(false));
      if(this.hasSignedout) { return EMPTY }
      this.hasSignedout = true
      this.#authService.logout();

      return throwError(
        () =>
          new Error(
            `Access token has expired - ${
              !authData
                ? 'Auth data not found'
                : !authData?.refreshToken
                ? 'No refresh token in auth data'
                : ''
            } - user will be logged out`
          )
      );
    }

    const tokenIsExpired = hasAccessTokenExpired(authData);

    if (!tokenIsExpired) {
      return next.handle(this.cloneRequestAndSetAuthorizationHeader(request))
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            const msg = 'Behörighet saknas. Prova att logga in igen.'
            const config = {
              progressBar: true,
              tapToDismiss: true,
              closeButton: true,
              disableTimeOut: false,
              timeout: 5000,
              positionClass: 'toast-top-center'
             }

             this.showToastr('warning', `${error.status} - ${error.statusText}`, msg, config)
             this.hasSignedout = true
             this.#authService.logout()
          } else if(error.status === 500) {
            //const msg = 'Ett fel inträffade. Försök igen eller kontakta support.'
            // const config = {
            //   progressBar: false,
            //   tapToDismiss: true,
            //   closeButton: true,
            //   disableTimeOut: true,
            //   positionClass: 'toast-top-center'
            //  }

            //  this.showToastr('error', `${error.status} - ${error.statusText}`, msg, config)

            const details =
              typeof error.error === 'string'
                ? error.error
                : error.error?.message ||
                  error.error?.error_description ||
                  JSON.stringify(error.error, null, 2) ||
                  error.message;

            console.error(`${error.status} - ${error.statusText}`, details);
          }

          return throwError(() => error);
    })
      )
    }

    return this.#authService.refreshToken().pipe(
      takeUntil(this.#authService.cancelPending$),
      switchMap(() => {
        const updatedReq = this.cloneRequestAndSetAuthorizationHeader(request);
        return next.handle(updatedReq);
      }),
      catchError((err) => {
        // already logged out in authService
        return throwError(() => err);
      })
    );
  }

  private showToastr(type: 'warning' | 'error' | 'info', title: string, message: string, toastrConfig?: any) {

    switch(type) {
      case 'warning':
        this.#toastrService.warning(message, title, toastrConfig)
        break;
      case 'error':
        this.#toastrService.error(message, title, toastrConfig)
        break;
      case 'info':
        this.#toastrService.info(message, title, toastrConfig)
        break;
    }

  }

  /** Add bearer token to request header */
  private cloneRequestAndSetAuthorizationHeader(
    request: HttpRequest<any>
  ): HttpRequest<any> {
    // Get AuthData
    let authData: AuthorizationData = this.#store.selectSnapshot(AuthContextSelectors.authContext);

    // Adds a bearer token in the header of a cloned request
    return request.clone({
      setHeaders: { Authorization: `Bearer ${authData?.token}` },
    });
  }
}
